import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  DeactivatedBanner,
  NoProjects,
  NoSelection,
  SidebarLoadingState,
  WorkspaceLoadError,
} from "components/common/Sidebar/components/SidebarStates";
import { Button } from "components/common/buttons/Button";
import { PATH_DASHBOARD } from "config";
import { useCreateProjectLauncher } from "hooks/project";
import { useProjectsApi } from "hooks/useProjects";
import { useSessionUser, useUserCatalog } from "hooks/useSession";
import { useWorkspaceApi, useWorkspaceIdentifiers } from "hooks/useWorkspace";
import { useWorkspaceAuth } from "hooks/useWorkspace/useWorkspaceAuth";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { Permissions } from "utils/permissions/permissionConstants";
import { AppSidebar } from "./AppSidebar";
import { useAppSidebarLayout } from "./appSidebarLayout";
import { AppSidebarFooter } from "./AppSidebarFooter";
import { AppSidebarHeader } from "./AppSidebarHeader";
import { useRememberProjectLocation } from "./lastProjectLocation";
import { ProjectsHomeNav } from "./ProjectsHomeNav";
import { ProjectSidebarNav } from "./project/ProjectSidebarNav";

/** Empty, loading and error states. Hidden when collapsed: 60px cannot hold the copy. */
const SidebarStateSlot = ({ children }: { children: React.ReactNode }) => {
  const { collapsed } = useAppSidebarLayout();
  if (collapsed) return null;
  return <div className="px-1 py-2">{children}</div>;
};

/**
 * The sidebar of the project area: the all-projects page and every project
 * page. It shows the production steps inside a project, and workspace
 * entries on the all-projects page. Project and draft switching live in
 * the header breadcrumb.
 */
export const ProjectAreaSidebar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { projectId: routeProjectId } = useParams<{ projectId?: string }>();
  const isProjectRoute = Boolean(routeProjectId && routeProjectId !== "0");
  const {
    launchCreateProject,
    createProjectFlowElement,
    canCreateWorkspaceProject,
    isWorkspaceAuthLoading,
  } = useCreateProjectLauncher();
  const { session } = useSessionUser();
  const {
    project,
    isLoading: projectLoading,
    isProjectError: projectLoadFailed,
    refetchProject,
  } = useProjectIdentifiers();
  const { workspaceId } = useWorkspaceIdentifiers();
  const {
    projects,
    isLoading: projectsLoading,
    isError: projectsLoadFailed,
  } = useProjectsApi({
    take: 10,
    order: "DESC",
    workspaceId,
  });
  const { refetch: refetchCatalog } = useUserCatalog();
  const {
    workspaces,
    isLoading: workspacesLoading,
    isError: workspacesError,
    error: workspacesErr,
    refetch: refetchWorkspaces,
  } = useWorkspaceApi({ take: 50 });

  const resolvedWorkspaceId = workspaceId || workspaces[0]?.id || "";
  const { canWorkspace, isLoading: workspacePermissionsLoading } =
    useWorkspaceAuth(resolvedWorkspaceId);
  const canCreateProject = !isWorkspaceAuthLoading && canCreateWorkspaceProject;
  const canOpenWorkspaceSettings =
    !workspacePermissionsLoading && canWorkspace(Permissions.WORKSPACE_READ);
  const openCreateProject = () => launchCreateProject();

  useRememberProjectLocation(
    isProjectRoute && project && resolvedWorkspaceId
      ? {
          workspaceId: resolvedWorkspaceId,
          projectId: String(project.projectId),
          projectName: project.name,
          path: pathname,
        }
      : null
  );

  const renderBody = (): React.ReactNode => {
    if (!resolvedWorkspaceId) {
      const message =
        (workspacesErr as { message?: string } | null)?.message ||
        (workspacesError ? "We couldn't load your workspaces." : "No workspace could be resolved.");
      return (
        <SidebarStateSlot>
          {workspacesLoading ? (
            <SidebarLoadingState ariaLabel="Loading workspace" />
          ) : (
            <WorkspaceLoadError message={message} onRetry={() => refetchWorkspaces()} />
          )}
        </SidebarStateSlot>
      );
    }

    if (session && !session.is_active) {
      return (
        <SidebarStateSlot>
          <DeactivatedBanner
            className=""
            onActivate={() => navigate(PATH_DASHBOARD.activateAccount)}
          />
        </SidebarStateSlot>
      );
    }

    if (!isProjectRoute) {
      return (
        <ProjectsHomeNav
          workspaceId={resolvedWorkspaceId}
          canCreateProject={canCreateProject}
          canOpenWorkspaceSettings={canOpenWorkspaceSettings}
          onCreateProject={openCreateProject}
        />
      );
    }

    if (projectLoading || (projectsLoading && !project)) {
      return (
        <SidebarStateSlot>
          <SidebarLoadingState ariaLabel="Loading project" />
        </SidebarStateSlot>
      );
    }

    if (!project) {
      return (
        <SidebarStateSlot>
          {/* A failed project or catalog request is unknown, not an empty workspace or no pick. */}
          {projectLoadFailed || projectsLoadFailed ? (
            <div role="alert" className="flex flex-col items-center gap-3 py-8 text-center">
              <p className="text-sm text-muted-foreground">
                {projectLoadFailed ? "Couldn't load this project" : "Couldn't load projects"}
              </p>
              <Button
                type="button"
                variant="badge"
                color="gray"
                size="sm"
                onClick={() => {
                  if (projectsLoadFailed) void refetchCatalog();
                  void refetchProject();
                }}
              >
                Try again
              </Button>
            </div>
          ) : projects.length === 0 ? (
            <NoProjects
              className=""
              onCreate={openCreateProject}
              canCreateProject={canCreateProject}
            />
          ) : (
            <NoSelection className="" />
          )}
        </SidebarStateSlot>
      );
    }

    return <ProjectSidebarNav workspaceId={resolvedWorkspaceId} project={project} />;
  };

  return (
    <>
      <AppSidebar
        label={isProjectRoute ? "Project navigation" : "Workspace navigation"}
        header={<AppSidebarHeader />}
        footer={<AppSidebarFooter />}
      >
        {renderBody()}
      </AppSidebar>
      {createProjectFlowElement}
    </>
  );
};
